import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Gift, MapPin, Sparkles, Navigation } from 'lucide-react';

const money = (c) => `RM${Math.round((c || 0) / 100)}`;

const Banner = ({ ev }) => {
  const soldOut = ev.sold_out;
  const closed = !ev.bookable && !soldOut;
  const cta = soldOut ? 'Sold Out' : closed ? 'Booking Closed' : ev.option_picker ? 'Select Your Session & Book Now' : `Book Now — ${money(ev.from_price_cents)}`;
  const gifts = useMemo(() => {
    const seen = new Set();
    return (ev.options || []).flatMap((o) => o.includes || []).filter((g) => (seen.has(g.key) ? false : seen.add(g.key)));
  }, [ev.options]);
  const minAvail = Math.min(...(ev.sessions || []).map((s) => s.available));
  return (
    <article
      className="rounded-2xl border-2 border-[#E9D5FF] bg-white overflow-hidden shadow-sm hover:shadow-lg hover:border-[#A855F7] transition-all"
      data-testid={`featured-class-${ev.slug}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr]">
        <Link to={`/events/${ev.slug}`} className="block bg-[#FAF5FF]" aria-label={`${ev.title} details`} data-testid={`featured-class-image-link-${ev.slug}`}>
          {ev.hero_image_url ? (
            <img src={ev.hero_image_url} alt={`${ev.headline || ev.title} — ${ev.title}`} className="w-full h-auto block" loading="lazy" />
          ) : (
            <div className="aspect-video flex items-center justify-center text-[#7E22CE] font-black">{ev.title}</div>
          )}
        </Link>
        <div className="p-6 md:p-8 flex flex-col">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#F3E8FF] text-[#4C1D95] text-[10px] font-black uppercase tracking-widest"><Sparkles size={10} /> {ev.option_picker ? 'Workshop · physical' : 'Featured physical class'}</span>
            {ev.venue_partner && <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-black uppercase tracking-widest" data-testid={`featured-venue-partner-${ev.slug}`}>Venue Partner · {ev.venue_partner}</span>}
          </div>
          {ev.headline && <p className="text-[#7E22CE] font-black text-lg md:text-xl leading-tight" data-testid={`featured-class-headline-${ev.slug}`}>{ev.headline}</p>}
          <h3 className="text-2xl md:text-3xl font-black tracking-tight leading-tight mt-1" data-testid={`featured-class-name-${ev.slug}`}>{ev.title}</h3>
          <div className="mt-4 space-y-2 text-sm text-slate-700">
            <div className="flex items-center gap-2"><Calendar size={15} className="text-[#A855F7] flex-shrink-0" /><span>{ev.date_label}</span></div>
            {(ev.schedule_lines || []).map((s) => (
              <div key={s.label} className="flex items-center gap-2"><Clock size={15} className="text-[#A855F7] flex-shrink-0" /><span><b>{s.label}:</b> {s.time}</span></div>
            ))}
            <div className="flex items-center gap-2 flex-wrap">
              <MapPin size={15} className="text-[#A855F7] flex-shrink-0" /><span>{ev.location} · Venue Partner: {ev.venue_partner}</span>
              {ev.directions_url && <a href={ev.directions_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#7E22CE] hover:underline" data-testid={`featured-directions-${ev.slug}`}><Navigation size={12} /> Get Directions</a>}
            </div>
            {gifts.map((g) => (
              <div key={g.key} className="flex items-start gap-2 text-emerald-700 font-semibold"><Gift size={15} className="flex-shrink-0 mt-0.5" /><span>{g.label}</span></div>
            ))}
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 pt-1">{ev.coach_name}</div>
          </div>
          {ev.option_picker && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid={`featured-options-${ev.slug}`}>
              {ev.options.map((o) => (
                <span key={o.key} className={`text-xs font-black px-2.5 py-1 rounded-full border ${o.sold_out ? 'border-slate-200 text-slate-400 line-through' : 'border-[#E9D5FF] bg-[#FAF5FF] text-[#4C1D95]'}`}>{o.label} · {money(o.price_cents)}{o.sold_out ? ' · Sold out' : ''}</span>
              ))}
            </div>
          )}
          <div className="mt-auto pt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/events/${ev.slug}`}
              className={`inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md text-white font-black uppercase tracking-widest text-xs ${soldOut || closed ? 'bg-slate-400' : 'bg-[#A855F7] hover:bg-[#7E22CE]'}`}
              data-testid={`featured-class-book-${ev.slug}`}
            >
              {cta} <ArrowRight size={14} />
            </Link>
            <span className="text-xs text-slate-500" data-testid={`featured-class-spots-${ev.slug}`}>
              {soldOut ? 'All seats taken' : `${ev.option_picker ? `From ${money(ev.from_price_cents)}` : `${money(ev.from_price_cents)} per participant`} · ${minAvail} seat${minAvail !== 1 ? 's' : ''} left`}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

/** Homepage / Upcoming Classes preview for event classes (from /api/event-classes). */
export const FeaturedClassBanner = ({ events, className = '' }) => {
  if (!events?.length) return null;
  return (
    <div className={`space-y-6 ${className}`} data-testid="featured-classes">
      {events.map((e) => <Banner key={e.slug} ev={e} />)}
    </div>
  );
};

export default FeaturedClassBanner;
